import type {
  AdminFundUpdateEntry,
  CmsBlock,
  FundUpdateEntry,
} from '../../../models/FundUpdateEntry';

/** Loads the unpublished (draft) content of an update, or null when it has none. */
export type LoadDraftContent = (updateId: string) => Promise<CmsBlock[] | null>;

const toEntry = (update: AdminFundUpdateEntry, content: CmsBlock[]): FundUpdateEntry => ({
  externalId: update.externalId,
  updateDate: update.updateDate,
  title: update.title ?? null,
  publishedAt: update.publishedAt,
  isLegacy: update.isLegacy,
  content,
  updateCard: update.updateCard ?? null,
});

/**
 * The fund's updates as investors would see them right after "Publish All": every draft is
 * published, every published update with pending edits shows those edits, and the rest are
 * unchanged. Newest first, matching the published-updates API the investor page reads.
 */
export async function mergeFundUpdatesAsPublished(
  published: AdminFundUpdateEntry[],
  drafts: AdminFundUpdateEntry[],
  loadDraftContent: LoadDraftContent,
): Promise<FundUpdateEntry[]> {
  const withDraftContent = async (update: AdminFundUpdateEntry) =>
    toEntry(update, (await loadDraftContent(update.externalId)) ?? update.content);

  const entries = await Promise.all([
    ...published.map((update) =>
      update.hasPendingDraft ? withDraftContent(update) : toEntry(update, update.content),
    ),
    ...drafts.map(withDraftContent),
  ]);

  return entries.sort((a, b) => b.updateDate.localeCompare(a.updateDate));
}
